import { useEffect, useState, useRef, Dispatch, SetStateAction } from "react";
import closeModal from "src/assets/images/common/closeModal.svg";
import dp from "src/assets/images/profile/dp.svg";
import check from "src/assets/images/bookings/check.svg";
import { IoWarning, IoDownloadOutline } from "react-icons/io5";
import { BsPlay, BsPause } from "react-icons/bs";
import { useParams } from "react-router-dom";
import bookingAdminService from "src/service/BookingAdmin";
import { useLoading } from "src/hooks";
import { ScaleLoader } from "react-spinners";

interface MessengersProps {
  artisan_meta: {
    first_name: string;
    last_name: string;
    display_picture: string;
    phone: string;
    business_name: string;
  };
  user_meta: {
    first_name: string;
    last_name: string;
    display_picture: string;
    phone: string;
  };
}

export const ChatModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  customerImg: string;
  vendorImg: string;
  messages: any[];
  setMessages?: Dispatch<SetStateAction<any[]>>;
  messengers: MessengersProps;
  b_type: string;
  //   business_name: string;
}> = ({
  open,
  handleClose,
  customerImg,
  vendorImg,
  messages,
  messengers,
  b_type,
  //   business_name,
}) => {
  const { loading } = useLoading(false);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
  const [audioProgress, setAudioProgress] = useState<{ [key: string]: number }>(
    {},
  );

  // Audio logic
  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName || "download");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const toggleAudioPlay = (messageId: string, url: string) => {
    if (playingAudio === messageId) {
      audioRefs.current[messageId]?.pause();
      setPlayingAudio(null);
    } else {
      if (playingAudio && audioRefs.current[playingAudio]) {
        audioRefs.current[playingAudio]?.pause();
      }
      if (!audioRefs.current[messageId]) {
        audioRefs.current[messageId] = new Audio(url);
        audioRefs.current[messageId].addEventListener("timeupdate", () => {
          const audio = audioRefs.current[messageId];
          const progress = (audio.currentTime / audio.duration) * 100;
          setAudioProgress((prev) => ({ ...prev, [messageId]: progress }));
        });
        audioRefs.current[messageId].addEventListener("ended", () => {
          setPlayingAudio(null);
          setAudioProgress((prev) => ({ ...prev, [messageId]: 0 }));
        });
      }
      audioRefs.current[messageId].play();
      setPlayingAudio(messageId);
    }
  };
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
    };
  }, []);
  const getFileNameFromUrl = (url: string) => {
    try {
      const urlParts = url.split("/");
      return urlParts[urlParts.length - 1];
    } catch (error) {
      return "file";
    }
  };
  // Helper for links
  const convertLinksToAnchors = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(
      urlRegex,
      (url) =>
        `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`,
    );
  };
  // Message content renderer
  const renderMessageContent = (messageObj: any) => {
    const { message_type, message, url, _id } = messageObj;
    if (message_type === "text") {
      return (
        <div
          className="text-sm leading-6 break-words"
          dangerouslySetInnerHTML={{ __html: convertLinksToAnchors(message) }}
        />
      );
    }
    if (message_type === "image") {
      return (
        <div className="relative max-w-[220px] rounded-lg overflow-hidden mb-1">
          <img src={url} alt="Image" className="w-full rounded-lg" />
          <button
            className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
            onClick={() => handleDownload(url, getFileNameFromUrl(url))}
          >
            <IoDownloadOutline size={18} />
          </button>
        </div>
      );
    }
    if (message_type === "audio") {
      return (
        <div className="w-full max-w-[220px] flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
          <button
            className={`flex items-center justify-center rounded-full w-8 h-8 ${
              messageObj.sender === "artisan"
                ? "bg-purple-600 text-white"
                : "bg-gray-300 text-purple-600"
            }`}
            onClick={() => toggleAudioPlay(_id, url)}
          >
            {playingAudio === _id ? (
              <BsPause size={18} />
            ) : (
              <BsPlay size={18} />
            )}
          </button>
          <div className="flex-1 h-1.5 bg-gray-300 rounded-full relative">
            <div
              className={`h-1.5 rounded-full ${
                messageObj.sender === "artisan"
                  ? "bg-purple-600"
                  : "bg-gray-400"
              }`}
              style={{ width: `${audioProgress[_id] || 0}%` }}
            ></div>
          </div>
          <button
            className="ml-2 text-gray-400 hover:text-purple-600"
            onClick={() => handleDownload(url, getFileNameFromUrl(url))}
          >
            <IoDownloadOutline size={18} />
          </button>
        </div>
      );
    }
    // Other file types
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200"
      >
        <span className="text-lg">📄</span>
        <span>{getFileNameFromUrl(url)}</span>
        <button
          className="ml-auto text-gray-400 hover:text-purple-600"
          onClick={(e) => {
            e.preventDefault();
            handleDownload(url, getFileNameFromUrl(url));
          }}
        >
          <IoDownloadOutline size={18} />
        </button>
      </a>
    );
  };

  // Modal not open: render nothing
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 transition-opacity"
        onClick={handleClose}
      />
      {/* Side Modal */}
      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 relative">
          <button
            className="mr-2 p-1 rounded-full hover:bg-gray-100"
            onClick={handleClose}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div className="flex items-center gap-2 flex-1 justify-center">
            <img
              src={customerImg}
              alt="customer"
              className="w-8 h-8 rounded-full border-2 border-white -mr-2 z-10"
            />
            <img
              src={vendorImg}
              alt="vendor"
              className="w-8 h-8 rounded-full border-2 border-white z-0"
            />
            <span className="ml-3 font-semibold text-gray-900">
              {messengers.user_meta.first_name} &{" "}
              {`${
                b_type === "individual"
                  ? `${messengers?.artisan_meta?.first_name} ${messengers?.artisan_meta?.last_name}`
                  : messengers?.artisan_meta?.business_name
              }` ||
                `
              ${messengers?.artisan_meta.first_name} $
              ${messengers?.artisan_meta.last_name}`}
              's Chat
            </span>
          </div>
          <button className="ml-auto p-1 rounded-full hover:bg-gray-100">
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="6" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="18" r="1.5" />
            </svg>
          </button>
        </div>
        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50 space-y-6">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <ScaleLoader color="#7E00C4" height={40} width={8} />
            </div>
          ) : messages.length > 0 ? (
            <div className="flex flex-col gap-6">
              {messages.map((message: any) => {
                const isVendor = message?.sender === "artisan";
                return (
                  <div
                    key={message?._id}
                    className={`flex ${
                      isVendor ? "items-start" : "flex-row-reverse items-end"
                    }`}
                  >
                    {/* Avatar */}
                    <img
                      src={isVendor ? vendorImg : customerImg}
                      alt="person"
                      className="w-8 h-8 rounded-full object-cover mt-1"
                    />
                    <div
                      className={`flex flex-col max-w-[70%] ${
                        isVendor ? "items-start ml-3" : "items-end mr-3"
                      }`}
                    >
                      {/* Name (optional) */}
                      <span
                        className={`text-xs font-semibold mb-1 ${
                          isVendor ? "text-purple-700" : "text-purple-700"
                        }`}
                      >
                        {isVendor
                          ? b_type === "individual"
                            ? `${messengers?.artisan_meta?.first_name} ${messengers?.artisan_meta?.last_name}`
                            : messengers?.artisan_meta?.business_name
                          : messengers.user_meta.first_name}
                      </span>
                      {/* Message bubble */}
                      <div
                        className={`mb-1 ${
                          message.message_type === "text"
                            ? isVendor
                              ? "bg-gray-100 text-gray-900"
                              : "bg-purple-600 text-white"
                            : isVendor
                              ? "bg-gray-100"
                              : "bg-purple-600"
                        } px-4 py-2 rounded-2xl shadow-sm text-sm break-words`}
                        style={{
                          borderBottomRightRadius: isVendor ? 16 : 4,
                          borderBottomLeftRadius: isVendor ? 4 : 16,
                        }}
                      >
                        {renderMessageContent(message)}
                      </div>
                      {/* Timestamp */}
                      <span className="text-xs text-gray-400 mt-1">
                        {message.message_date}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex justify-center items-center py-10 px-5">
              <span className="text-sm px-5 py-3 flex items-center leading-5 rounded-lg bg-gray-50 text-gray-500">
                <IoWarning size={20} /> There are currently no available chat
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatModal;
